import React, { useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';

import 'react-quill/dist/quill.snow.css';
import 'quill-mention/dist/quill.mention.css';
import './RichTextEditor.css';

// Import whatever the package gives us (default in your case)
import MentionModule from 'quill-mention';
import { User } from '../../../interfaces/User';
import { useAvatarCache } from '../../../contexts/AvatarContext';

// Normalize both ESM and CJS shapes:
const Mention = (MentionModule as any).Mention ?? MentionModule;
const MentionBlot = (MentionModule as any).MentionBlot ?? (MentionModule as any).Blot ?? undefined;

// Register safely (only register the blot if we found it)
Quill.register({ 'modules/mention': Mention });
if (MentionBlot) {
    Quill.register({ 'blots/mention': MentionBlot });
}

type ToolbarVariant = 'full' | 'simple';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    users?: User[]; // For mentions
    placeholder?: string;
    readOnly?: boolean;
    toolbarVariant?: ToolbarVariant;
}


const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, users = [], placeholder, readOnly, toolbarVariant = 'full' }) => {
    const usersRef = useRef(users);
    usersRef.current = users;
    const { getAvatarUrl } = useAvatarCache();

    const fullToolbar = [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
        ['link', 'code-block'],
        ['clean']
    ];

    const simpleToolbar = [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
    ];

    const modules = useMemo(() => ({
        toolbar: toolbarVariant === 'simple' ? simpleToolbar : fullToolbar,
        mention: {
            allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
            mentionDenotationChars: ["@"],
            source: (searchTerm: string, renderList: (values: any[], searchTerm: string) => void) => {
                const currentUsers = usersRef.current;
                let matches: User[];
                if (searchTerm.length === 0) {
                    matches = currentUsers;
                } else {
                    const lowerCaseSearchTerm = searchTerm.toLowerCase();
                    matches = currentUsers.filter(user =>
                        user.displayName.toLowerCase().includes(lowerCaseSearchTerm) ||
                        (user.email && user.email.toLowerCase().includes(lowerCaseSearchTerm))
                    );
                }

                const promises = matches.map(user =>
                    getAvatarUrl(user.id)
                        .catch(() => null) // On error, resolve with null
                        .then(avatarSrc => ({
                            id: user.id,
                            value: user.displayName,
                            email: user.email,
                            avatar: avatarSrc
                        }))
                );

                Promise.all(promises).then(usersWithAvatars => {
                    renderList(usersWithAvatars, searchTerm);
                });
            },
            renderItem: (item: any) => {
                const initials = (item.value || '').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();
                const avatarDisplay = item.avatar
                    ? `<img src="${item.avatar}" class="avatar-image" alt="Avatar for ${item.value}" />`
                    : `<div class="default-avatar">${initials}</div>`;

                return `
                    <div class="mention-list-item-container">
                        <div class="avatar-placeholder">
                            ${avatarDisplay}
                        </div>
                        <div class="user-details">
                            <div class="user-name">${item.value}</div>
                            <div class="user-email">${item.email || ''}</div>
                        </div>
                    </div>
                `;
            },
            onSelect: (item: any, insertItem: (data: { id: string, value: string, denotationChar: string }) => void) => {
                insertItem({ id: item.id, value: item.value, denotationChar: '@' });
            },
        },
    }), [getAvatarUrl]);

    const formats =
        toolbarVariant === 'simple'
            ? ['bold', 'italic', 'underline', 'list', 'bullet']
            : [
                'header',
                'bold', 'italic', 'underline', 'strike', 'blockquote',
                'list', 'bullet', 'indent',
                'link', 'code-block', 'mention',
            ];


    return (
        <ReactQuill
            theme="snow"
            value={value}
            onChange={onChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder || 'Describe the issue...'}
            readOnly={readOnly}
        />
    );
};

export default RichTextEditor;
